import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const ContactContainer = styled.div`
  padding: 8rem 2rem 6rem;
  /* Assurer que la page prend au minimum toute la hauteur de la vue */
  min-height: calc(100vh - 14rem); /* Ajusté pour la navigation et le footer */
  
  @media (max-width: 768px) {
    padding: 6rem 1rem 4rem;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.accent});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 2rem;
`;

const ContactInfo = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  max-width: 600px;
`;

const ContactTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.colors.text};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.8rem;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: ${({ theme }) => theme.colors.primary};
  }
`;

const ContactDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 2.5rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ContactMethod = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  
  svg {
    width: 24px;
    height: 24px;
    margin-right: 1rem;
    fill: ${({ theme }) => theme.colors.primary};
  }
  
  span {
    font-size: 1.1rem;
    color: ${({ theme }) => theme.colors.text};
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const SocialIcon = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  transition: all 0.2s ease;
  
  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
  }
`;

// Ajout d'un style pour le message de maintenance
const FormDisabledOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => `${theme.colors.background}cc`};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  z-index: 10;
  border-radius: 12px;
  backdrop-filter: blur(3px);
`;

const FormDisabledMessage = styled.div`
  text-align: center;
  max-width: 400px;
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.colors.primary};
  }
  
  p {
    color: ${({ theme }) => theme.colors.textSecondary};
    margin-bottom: 1rem;
  }
  
  svg {
    width: 40px;
    height: 40px;
    fill: ${({ theme }) => theme.colors.accent};
    margin-bottom: 1.5rem;
  }
`;

const ContactForm = styled(motion.form)`
  background-color: ${({ theme }) => `${theme.colors.background}cc`}; /* Ajout de transparence (80%) */
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
  position: relative; /* Ajout pour le positionnement de l'overlay */
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem 1rem;
  background-color: ${({ theme }) => `${theme.colors.backgroundLight}dd`}; /* Ajout de transparence (87%) */
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}30;
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary}80;
  }
  
  @media (max-width: 480px) {
    padding: 0.7rem 0.9rem;
    font-size: 16px; /* Évite le zoom automatique sur iOS */
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.8rem 1rem;
  background-color: ${({ theme }) => `${theme.colors.backgroundLight}dd`}; /* Ajout de transparence (87%) */
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}30;
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary}80;
  }
`;

const FileInput = styled.div`
  position: relative;
  margin-top: 1rem;
  
  input[type="file"] {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
  
  .file-custom {
    display: flex;
    align-items: center;
    padding: 0.8rem 1rem;
    background-color: ${({ theme }) => `${theme.colors.backgroundLight}dd`}; /* Ajout de transparence (87%) */
    border: 1px dashed ${({ theme }) => theme.colors.border};
    border-radius: 6px;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 0.9rem;
    
    svg {
      margin-right: 0.5rem;
      width: 18px;
      height: 18px;
      fill: ${({ theme }) => theme.colors.textSecondary};
    }
  }
  
  &:hover .file-custom {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SubmitButton = styled(motion.button)`
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.accent});
  color: white;
  border: none;
  padding: 0.8rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 1rem;
  width: 100%;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
  }
  
  &:focus {
    outline: none;
  }
`;

const ThankYouMessage = styled(motion.div)`
  text-align: center;
  padding: 2rem;
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.colors.primary};
  }
  
  p {
    color: ${({ theme }) => theme.colors.textSecondary};
    margin-bottom: 2rem;
  }
  
  svg {
    width: 60px;
    height: 60px;
    fill: ${({ theme }) => theme.colors.success};
    margin-bottom: 1.5rem;
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error || '#ff5555'};
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const LoadingSpinner = styled.div`
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
  margin-right: 10px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FileList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0;
`;

const FileItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px dashed ${({ theme }) => theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const FileName = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 85%;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.error || '#ff5555'};
  cursor: pointer;
  padding: 0.2rem;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const FooterLink = styled(motion.a)`
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  margin-bottom: 0.75rem;
  transition: all 0.2s ease;
  display: inline-block;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    files: [] as File[]
  });
  const [submitted, setSubmitted] = useState(false);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  // Constante pour désactiver le formulaire
  const isFormDisabled = true;
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      
      // Vérification du type de fichier (images ou PDF)
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf'];
      const allowedMimeTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      
      if (!allowedExtensions.includes(fileExt || '') || !allowedMimeTypes.includes(file.type)) {
        setFileError(t('contact:form.errors.fileType'));
        return;
      }
      
      // Vérification de la taille (5 Mo max)
      if (file.size > 5 * 1024 * 1024) {
        setFileError(t('contact:form.errors.fileSize'));
        return;
      }
      
      // Limite le nombre de fichiers à 5
      if (formState.files.length >= 5) {
        setFileError(t('contact:form.errors.fileLimit'));
        return;
      }

      // Vérifier si le fichier existe déjà dans la liste
      if (formState.files.some(f => f.name === file.name)) {
        setFileError(t('contact:form.errors.fileDuplicate'));
        return;
      }
      
      setFormState(prev => ({ ...prev, files: [...prev.files, file] }));
      setFileNames(prev => [...prev, file.name]);
      setFileError(null);
      
      // Réinitialiser l'input file pour permettre de sélectionner le même fichier à nouveau
      e.target.value = '';
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Réinitialisation des erreurs
    setError(null);
    
    // Vérification des fichiers attachés si présent
    if (formState.files.length > 0 && fileError) {
      return; // Ne pas envoyer si le fichier est invalide
    }
    
    setIsLoading(true);
    
    try {
      // Préparation des données du formulaire pour l'envoi
      const formData = new FormData();
      formData.append('name', formState.name);
      formData.append('email', formState.email);
      formData.append('subject', formState.subject);
      formData.append('message', formState.message);
      
      // Ajout des fichiers s'ils existent
      if (formState.files.length > 0) {
        // Pour l'envoi de plusieurs fichiers, nous devons donner le même nom mais avec des crochets à la fin
        formState.files.forEach((file, index) => {
          formData.append('file-upload[]', file);
        });
      }
      
      // Logs pour débogage
      console.log('Données du formulaire:', {
        name: formState.name,
        email: formState.email,
        subject: formState.subject,
        messageLength: formState.message.length,
        filesAttached: formState.files.map(file => file.name)
      });
      
      // Détecter l'environnement (développement ou production)
      // En local, on détecte si l'URL contient localhost ou est 127.0.0.1
      const isDevelopment = process.env.NODE_ENV === 'development' || 
                           window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1';
      console.log('Environnement détecté:', isDevelopment ? 'Développement' : 'Production');
      console.log('Hostname:', window.location.hostname);
      console.log('NODE_ENV:', process.env.NODE_ENV);
      
      // En développement, on simule l'envoi d'email
      if (isDevelopment) {
        console.log('Mode développement: simulation d\'envoi d\'email');
        // Simuler un délai d'envoi
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        console.log('Email simulé envoyé à:', 'contact@maximegermond.com');
        console.log('De:', formState.email);
        console.log('Sujet:', formState.subject);
        console.log('Message:', formState.message);
        
        // Afficher toutes les pièces jointes dans les logs
        if (formState.files.length === 0) {
          console.log('Pièces jointes: Aucune');
        } else {
          console.log('Pièces jointes:');
          formState.files.forEach((file, index) => {
            console.log(`  ${index + 1}. ${file.name} (${Math.round(file.size / 1024)} KB)`);
          });
        }
        
        // Considérer comme réussi en développement
        setSubmitted(true);
        setFormState({
          name: '',
          email: '',
          subject: '',
          message: '',
          files: []
        });
        setFileNames([]);
      } 
      // En production, on envoie réellement l'email via PHP
      else {
        const phpUrl = '/assets/php/contact.php';
        console.log('Envoi du formulaire à:', phpUrl);
        
        // Envoi de la requête au script PHP avec les en-têtes CORS appropriés
        const response = await fetch(phpUrl, {
          method: 'POST',
          body: formData,
          headers: {
            'Accept': 'text/plain, */*',
            // Ne pas définir Content-Type, le navigateur le définira automatiquement avec le boundary nécessaire pour FormData
          },
          credentials: 'same-origin', // Pour inclure les cookies PHPSESSID
        });
        
        // Vérification de la réponse du serveur
        const result = await response.text();
        console.log('Réponse du serveur:', result);
        
        if (response.ok) {
          // Si la réponse contient "succès", on considère que l'envoi est réussi
          if (result.toLowerCase().includes('succ')) {
            setSubmitted(true);
            
            // Réinitialisation du formulaire
            setFormState({
              name: '',
              email: '',
              subject: '',
              message: '',
              files: []
            });
            setFileNames([]);
          } else {
            // Sinon c'est une erreur côté serveur
            setError(result || t('contact:form.errors.serverError'));
          }
        } else {
          // Erreur de la requête
          setError(t('contact:form.errors.connectionError'));
        }
      }
    } catch (err) {
      // Erreur technique
      console.error('Erreur détaillée:', err);
      setError(t('contact:form.errors.technicalError'));
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <ContactContainer>
      <SectionTitle
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {t('contact:pageTitle')}
      </SectionTitle>
      
      <ContentWrapper>
        <ContactInfo
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <ContactTitle>{t('contact:subtitle')}</ContactTitle>
          <ContactDescription>
            {t('contact:description')}
          </ContactDescription>
          <ContactMethod>
            <svg viewBox="0 0 24 24">
              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
            </svg>
            <span><a href="mailto:contact@maximegermond.com" style={{ color: 'inherit', textDecoration: 'none' }}>contact@maximegermond.com</a></span>
          </ContactMethod>
          
          <ContactMethod>
            <svg viewBox="0 0 24 24">
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
            </svg>
            <span><a href="tel:+33662576727" style={{ color: 'inherit', textDecoration: 'none' }}>+33 6 62 57 67 27</a></span>
          </ContactMethod>
          
          <ContactMethod>
            <svg viewBox="0 0 24 24">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
            <span>{t('contact:location')}</span>
          </ContactMethod>
          
          <SocialLinks>
            <SocialIcon 
              href="https://www.linkedin.com/in/maxime-germond-02445a263/" 
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -5 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <svg viewBox="0 0 24 24">
                <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
              </svg>
            </SocialIcon>
            
            <SocialIcon 
              href="https://github.com/GRD-Maxime" 
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -5 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
            </SocialIcon>
          </SocialLinks>
        </ContactInfo>
        
        {/* Le formulaire est intentionnellement omis car il est désactivé (isFormDisabled) */}
      </ContentWrapper>
    </ContactContainer>
  );
};

export default ContactPage;